import {
    Box,
    Checkbox,
    SxProps,
    TableCell,
    TableRow,
    Theme,
    Typography,
} from '@mui/material';
import withObservables from '@nozbe/with-observables';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
    MouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { of } from 'rxjs/internal/observable/of';
import { INVOICE_STATUS } from 'shared/constants/invoices/statuses';
import DatabaseService from 'shared/db/services/Database.web';
import OrganisationAccountings from 'shared/db/services/OrganisationAccountings';
import { DBWriterLoggerFunction } from 'shared/types/logger';
import {
    calculateInvoiceDetails,
    getInvoiceBusinessOrContactName,
    getInvoiceStatusColor,
} from 'shared/utils/invoicing';

import { PersonIcon } from '@/assets/svg';
import { useImagesContext } from '@/context/ImagesContext';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useInvoiceStatusLabel } from '@/hooks';
import { useRegion } from '@/hooks/useRegion';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import { dbServiceLogger } from '@/services/logger/logger';
import { COLOR } from '@/theme/colors';
import { formatDate } from '@/utils/date';
import {
    calculateInvoicePriceSum,
    getOverdueWithColor,
} from '@/utils/invoicing';
import { getRoutePath } from '@/utils/router';

import { Props } from './types';

const contactCellStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
};

const cellTextStyle: SxProps<Theme> = {
    fontSize: 13,
    fontWeight: 300,
};

const database = DatabaseService.getDatabase();

function InvoicesTableItem({
    invoice,
    contacts,
    organisationAccountingsData,
    selectable,
    isSelected,
    onSelect,
    navigationState,
}: Props) {
    const navigate = useNavigate();

    const { ImagesService } = useImagesContext();
    const { isInvoiceSystemEnabled } = useInvoicingSystemContext();

    const [sumTotal, setSumTotal] = useState('');

    const userRegion = useRegion();
    const currency = userRegion ? userRegion.currencySymbol : '$';

    const accountings = organisationAccountingsData
        ? organisationAccountingsData[0]
        : null;
    const contact = contacts ? contacts[0] : undefined;

    const invoiceStatusLabel = useInvoiceStatusLabel(
        invoice,
        !!(accountings && accountings.accountingProvider),
    );

    const navigateToInvoice = useCallback(() => {
        const path = getRoutePath(ROUTE.invoice, { id: invoice.id });

        navigate(
            path,
            navigationState ? { state: navigationState } : undefined,
        );
    }, [invoice.id, navigate, navigationState]);

    const handleCheckboxClick = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            onSelect();
        },
        [onSelect],
    );

    const handleInvoiceItemClick = useCallback(
        (event: MouseEvent) => {
            if (selectable) {
                handleCheckboxClick(event);
            } else {
                navigateToInvoice();
            }
        },
        [handleCheckboxClick, navigateToInvoice, selectable],
    );

    useEffect(() => {
        let active = true;

        async function getInvoiceSumTotal() {
            const { subtotalSum, totalSum } = await calculateInvoiceDetails(
                {
                    database,
                    imageService: ImagesService,
                    logger: dbServiceLogger,
                    validation: databaseValidation,
                },
                invoice,
            );

            if (!active) return;

            const taxIncluded = isInvoiceSystemEnabled && invoice.applyTaxes;
            const priceSum = taxIncluded ? totalSum : subtotalSum;

            const price = calculateInvoicePriceSum({
                priceSum,
                invoice,
                currency,
            });
            setSumTotal(price);
        }

        getInvoiceSumTotal();

        return () => {
            active = false;
        };
    }, [ImagesService, currency, invoice, isInvoiceSystemEnabled]);

    const buildOverdueField = useMemo(() => {
        const { color, value } = getOverdueWithColor(
            invoice.dueDateTime,
            invoice.status,
        );

        return (
            <Typography
                data-test-id="InvoicesPage-TableRow-Overdue"
                sx={{
                    color,
                    fontSize: 13,
                    fontWeight: 400,
                }}
            >
                {value}
            </Typography>
        );
    }, [invoice.dueDateTime, invoice.status]);

    const invoiceStatusColor = useMemo(() => {
        return getInvoiceStatusColor(invoice.status, invoice.dueDateTime).color;
    }, [invoice.status, invoice.dueDateTime]);

    if (!invoice) return null;

    return (
        <TableRow
            className="transition-transform hover:scale-[1.01] hover:cursor-pointer hover:bg-blue-50/50 child:border-0 last-child:rounded-r-lg first-child:rounded-l-lg"
            data-test-id={`InvoicesPage-TableRow-${contact?.firstName}`}
            onClick={handleInvoiceItemClick}
        >
            <TableCell component="th" scope="row" width="28%">
                <Box sx={contactCellStyles}>
                    <div className="flex flex-row items-center">
                        {selectable ? (
                            <Checkbox
                                name={`${contact?.firstName}-checkbox`}
                                checked={isSelected}
                                onClick={handleCheckboxClick}
                                size="small"
                                sx={{ ml: '-9px' }}
                            />
                        ) : null}
                        <Typography
                            data-test-id="InvoicesPage-TableRow-ContactInfo"
                            sx={{
                                fontWeight: 700,
                                fontSize: 16,
                                lineHeight: '24px',
                                color:
                                    invoice.status === INVOICE_STATUS.draft
                                        ? COLOR.vividBlack
                                        : invoiceStatusColor,
                            }}
                        >
                            {invoice.reference}
                        </Typography>
                    </div>
                    {invoice.number ? (
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 400,
                                lineHeight: '24px',
                                color: COLOR.paleSky,
                            }}
                        >
                            {invoice.number}
                        </Typography>
                    ) : null}
                    <div className="flex flex-row gap-1 items-center">
                        <PersonIcon width={14} height={14} />
                        <Typography
                            className="text-xs"
                            sx={{
                                color: COLOR.vscBlack,
                                fontWeight: 400,
                                lineHeight: '19.5px',
                            }}
                        >
                            {getInvoiceBusinessOrContactName(contact)}
                        </Typography>
                    </div>
                </Box>
            </TableCell>
            <TableCell align="left" width="10%">
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        lineHeight: '24px',
                        color: COLOR.vividBlack,
                    }}
                    data-test-id="InvoicesPage-TableRow-Total"
                >
                    {sumTotal ? `${sumTotal}` : '-'}
                </Typography>
            </TableCell>
            <TableCell align="left" width={'15%'}>
                {buildOverdueField}
            </TableCell>
            <TableCell align="left">
                <div className="flex flex-row items-center">
                    {invoiceStatusLabel}
                </div>
            </TableCell>
            <TableCell width="23%">
                <Typography
                    sx={cellTextStyle}
                    data-test-id="InvoicesPage-TableRow-CreatedAt"
                >
                    {formatDate(invoice.createdAt.toISOString())}
                </Typography>
            </TableCell>
            <TableCell width="23%">
                <Typography
                    sx={cellTextStyle}
                    data-test-id="InvoicesPage-TableRow-UpdatedAt"
                >
                    {formatDate(invoice.updatedAt.toISOString())}
                </Typography>
            </TableCell>
        </TableRow>
    );
}

const enhance = withObservables<Props, unknown>(['invoice'], ({ invoice }) => {
    const database = DatabaseService.getDatabase();
    const logDBActionMock: DBWriterLoggerFunction = () => {
        return;
    };

    const accountingsService = new OrganisationAccountings({
        database,
        logDBAction: logDBActionMock,
        validation: databaseValidation,
    });

    return {
        invoice: invoice ? invoice.observe() : of([]),
        contacts: invoice ? invoice.contacts.observe() : of([]),
        organisationAccountingsData: accountingsService.get(),
    };
});

export default enhance(InvoicesTableItem);
